/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import { Box, ScrollArea, useMantineTheme } from '@mantine/core'
import { useElementSize, useMediaQuery } from '@mantine/hooks'
import { flexRender, Row } from '@tanstack/react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso, TableVirtuosoProps } from 'react-virtuoso'

import { DEFAULT_TABLE_PROPS, SELECT_COLUMN_ID } from '../constants'
import useScrollAreaStyles from '../styles/useScrollAreaStyles'
import useVirtuosoTableStyles from '../styles/useVirtuosoTableStyles'
import Header from './Header'
import { DefaultPaginatedTablProps } from './paginated-table'
import SelectionFooter from './SelectionFooter'
import { VirtuosoTableContext } from './Virtuoso/virtuoso-table-sections'
import VirtuosoEmptyPlaceholder from './Virtuoso/VirtuosoEmptyPlaceHolder'
import VirtuosoMantineTable from './Virtuoso/VirtuosoMantineTable'
import VirutosoTableFoot from './Virtuoso/VirtuosoTableFoot'
import VirtuosoTableHead from './Virtuoso/VirtuosoTableHead'
import VirutosoTableRow from './Virtuoso/VirtuosoTableRow'
export const Components: TableVirtuosoProps<
  Row<any>,
  VirtuosoTableContext<any>
>['components'] = {
  Table: (props) => {
    const { context, ...rest } = props
    if (!context) return <></>

    return (
      <VirtuosoMantineTable
        height={context.height}
        minTableWidth={context.minTableWidth}
        {...rest}
        tableProps={context.tableProps}
      />
    )
  },

  TableFoot: React.forwardRef((props, ref) => {
    const { context, ...rest } = props
    if (!context) return <></>

    return (
      <VirutosoTableFoot
        tableProps={context.tableProps}
        selectionState={context.selectionState}
        ref={ref}
        {...rest}
      />
    )
  }),

  TableHead: React.forwardRef((props, ref) => {
    const { context, ...rest } = props
    if (!context) return <></>

    return (
      <VirtuosoTableHead
        tableProps={context.tableProps}
        scrolled={context.scrolled}
        ref={ref}
        {...rest}
      />
    )
  }),

  TableRow: (props) => {
    const { context, item: _item, ...rest } = props

    if (!context) return <></>

    return <VirutosoTableRow {...rest} />
  },

  EmptyPlaceholder: (props) => {
    const { context, ...rest } = props

    if (!context) return <></>
    return (
      <VirtuosoEmptyPlaceholder
        tableInstance={context.tableInstance}
        paginatedQuery={context.paginatedQuery}
        {...rest}
      />
    )
  },
}

const PaginatedTable = <T,>({
  tableInstance,
  sortFilter,
  tableProps = DEFAULT_TABLE_PROPS,
  paginatedQuery,
  selectionState,
  allIdsOnActivePage,
  selectionActions,
}: DefaultPaginatedTablProps<T>): React.ReactElement => {
  const [scrolled, setScrolled] = useState(false)
  const [xScrollable, setXScrollable] = useState(false)
  const { ref: tableWrapperRef, height, width } = useElementSize()

  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null)

  const { classes: scrollAreaClasses, cx } = useScrollAreaStyles({
    height,
  })
  const { classes: virtuosoTableClasses } = useVirtuosoTableStyles()
  const rows = tableInstance.getRowModel().rows

  const setScrollParentRef = useCallback(
    (r: HTMLDivElement | null) => {
      if (r) {
        setScrollParent(r)
      }
    },
    [setScrollParent],
  )

  const setScrolledState = useCallback(
    ({ y }: { x: number; y: number }) => {
      setScrolled(y !== 0)
    },
    [setScrolled],
  )

  const minColWidth = useMemo(() => {
    return rows[0]
      ? rows[0]
          .getVisibleCells()
          .map((c) => c.column.getSize())
          .reduce((a, b) => a + b, 0)
      : 0
  }, [rows])

  const theme = useMantineTheme()

  const is1080pOrLess = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  useEffect(() => {
    if (scrollParent) {
      setXScrollable(scrollParent.scrollWidth > scrollParent.clientWidth)
    }
  }, [setScrollParentRef])

  return (
    <Box
      ref={tableWrapperRef}
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ScrollArea
        viewportRef={setScrollParentRef}
        sx={() => ({
          height,
          minHeight: height,
          width,
        })}
        classNames={{
          root: scrollAreaClasses.scrollAreaRoot,
          viewport: cx(scrollAreaClasses.scrollAreaWidthFix, {
            [scrollAreaClasses.zeroBottomPadding]: !xScrollable,
          }),
          scrollbar: scrollAreaClasses.scrollbars,
        }}
        offsetScrollbars
        onScrollPositionChange={setScrolledState}
      >
        <TableVirtuoso<Row<T>, VirtuosoTableContext<T>>
          context={{
            height,
            minTableWidth: minColWidth,
            tableProps: {
              ...tableProps,
              verticalSpacing: is1080pOrLess ? 'xs' : 'md',
              horizontalSpacing: is1080pOrLess ? 'xs' : 'md',
            },
            scrolled,
            selectionState,
            paginatedQuery,
            tableInstance,
          }}
          totalCount={rows.length}
          components={Components}
          style={{
            height,
          }}
          data={rows}
          fixedHeaderContent={() => (
            <Header
              tableInstance={tableInstance}
              tableProps={tableProps}
              sortFilter={sortFilter}
            />
          )}
          fixedFooterContent={() =>
            selectionState ? (
              <SelectionFooter
                selectionState={selectionState}
                columnsCount={tableInstance.getAllColumns().length}
                allIdsOnActivePage={allIdsOnActivePage}
                selectionActions={selectionActions}
              />
            ) : null
          }
          itemContent={(_index, row) => {
            return (
              <>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={`cell-${cell.id}`}
                    style={{
                      minWidth: cell.column.columnDef.minSize ?? 'auto',
                      width: cell.column.getSize() ?? 'auto',
                      maxWidth: cell.column.columnDef.maxSize ?? 'auto',
                    }}
                    align={cell.column.columnDef.meta?.align ?? 'left'}
                    className={cx(virtuosoTableClasses.tdText, {
                      [virtuosoTableClasses.zeroPaddingTd]:
                        cell.column.id === SELECT_COLUMN_ID,
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </>
            )
          }}
          customScrollParent={scrollParent ?? undefined}
        />
      </ScrollArea>
    </Box>
  )
}

export default PaginatedTable
